<template>
  <div class="page-background">
    <div class="page-container main-container centered md-elevation-2">
      <md-toolbar class="md-dense">
        <h4>{{ $config.invoice.title }}</h4>
        &nbsp;&nbsp;&nbsp;
        {{ L('invoice') }} {{ L('no') }} {{ invoice.seq }}
      </md-toolbar>
      <md-table>
        <md-table-row>
          <md-table-cell>{{ L('name') }} ({{ L('company') }})</md-table-cell>
          <md-table-cell
            >{{ invoice.name }} ({{ invoice.company }})</md-table-cell
          >
        </md-table-row>
        <md-table-row>
          <md-table-cell>{{ L('invoiceMonth') }}</md-table-cell>
          <md-table-cell>{{ invoice.invoice_month }}</md-table-cell>
        </md-table-row>
        <md-table-row>
          <md-table-cell>{{ L('totalCost') }}</md-table-cell>
          <md-table-cell md-numeric>{{
            invoice.cost | round2 | currency(invoice.currency)
          }}</md-table-cell>
        </md-table-row>
      </md-table>

      <br />
      <br />
      <h1>&nbsp;&nbsp;&nbsp;{{ L('overview') }}</h1>

      <md-table
        v-if="invoice.usage_data && invoice.usage_data.length > 0"
        v-model="invoice.usage_data"
        md-stripe
        md-thin
      >
        <md-table-row
          slot="md-table-row"
          slot-scope="{ item }"
          @click="scrollMeTo(item.idx)"
        >
          <md-table-cell
            :md-label="L(hasProject() ? 'projectId' : 'serviceId')"
            >{{
              hasProject() ? item.project_id : item.service_id
            }}</md-table-cell
          >
          <md-table-cell
            :md-label="L(hasProject() ? 'projectName' : 'serviceName')"
            >{{
              hasProject() ? item.project_name : item.service_name
            }}</md-table-cell
          >
          <md-table-cell :md-label="L('totalCost')" md-numeric>{{
            item.total_cost | round2 | currency(item.currency)
          }}</md-table-cell>
        </md-table-row>
      </md-table>

      <div
        v-for="usage in invoice.usage_data"
        :key="hasProject() ? usage.project_id : usage.service_id"
      >
        <br />
        <br />
        <h1 ref="projects">
          &nbsp;&nbsp;&nbsp;
          {{
            hasProject()
              ? `${usage.project_name} [${usage.project_id}]`
              : `${usage.service_name} [${usage.service_id}]`
          }}
        </h1>

        <md-table v-model="usage.data" md-stripe md-thin>
          <md-table-row slot="md-table-row" slot-scope="{ item }">
            <md-table-cell
              v-if="hasProject()"
              :md-label="L('service')"
              style="width: 200px"
              >{{ item.service_name }}</md-table-cell
            >
            <md-table-cell
              v-else
              :md-label="L('product')"
              style="width: 300px"
              >{{ item.sku_name || 'N/A' }}</md-table-cell
            >
            <md-table-cell :md-label="L(hasProject() ? 'product' : 'type')">{{
              hasProject() ? item.sku_name : item.cost_type
            }}</md-table-cell>
            <md-table-cell :md-label="L('startDate')" style="width: 130px">{{
              item.usage_start_date
            }}</md-table-cell>
            <md-table-cell :md-label="L('endDate')" style="width: 130px">{{
              item.usage_end_date
            }}</md-table-cell>
            <md-table-cell
              :md-label="L('cost')"
              md-numeric
              style="width: 100px"
              >{{
                item.cost | round2 | currency(usage.currency)
              }}</md-table-cell
            >
          </md-table-row>
        </md-table>
      </div>
    </div>
  </div>
</template>
<script>
import api from '@/api'
import { changePath } from '@/lib/textutil'

export default {
  data: () => ({
    invoice: { data: [] },
    usageData: [],
  }),

  props: {
    invoiceSeq: String,
    userSeq: String,
    lang: String,
  },

  mounted() {
    this.$i18n.locale =
      ['en', 'ko'].indexOf(this.lang) !== -1 ? this.lang : 'en'
    this.load()
  },

  created() {
    const settingInfo = JSON.parse(localStorage.getItem('settingInfo')) || {}
    const media = window.matchMedia('(prefers-color-scheme: Dark)')
    if (settingInfo.appearance === 'auto') {
      media.addEventListener('change', (e) => {
        this.switchTheme(e.matches)
      })
      this.switchTheme(media.matches)
    } else {
      this.switchTheme(settingInfo.appearance === 'dark')
    }
  },

  methods: {
    L(path) {
      const changePathText = changePath(path)

      return this.$te(changePathText)
        ? this.$t(changePathText)
        : this.$t(`billing.invoiceDetail.${path}`)
    },
    load() {
      api.session.info().then((res) => {
        if (Number(this.userSeq) === res.seq) {
          return api.user
            .invoiceDetail(this.userSeq, this.invoiceSeq)
            .then((data) => this.putUsageData(data))
        }
        if (!this.userSeq) {
          return api.invoice
            .get(this.invoiceSeq)
            .then((data) => this.putUsageData(data))
        }
        return []
      })
    },

    putUsageData(data) {
      if (!data) return
      const usageData = data.usage_data
      data.original_cost = 0
      usageData.forEach((item) => {
        item.total_cost = item.data.reduce((p, e) => p + e.cost, 0)
        item.data.sort((a, b) => b.cost - a.cost)
        item.data.push({
          service_id: 'Total',
          service_name: 'Total',
          sku_id: this.hasProject() ? '' : 'Total',
          sku_name: this.hasProject() ? '' : 'Total',
          cost: item.total_cost,
          currency: item.currency,
        })
        data.original_cost += item.total_cost
      })
      usageData.sort((a, b) => b.total_cost - a.total_cost)
      usageData.forEach((item, i) => {
        item.idx = i
      })

      this.invoice = data
      this.usageData = usageData
    },

    scrollMeTo(idx) {
      const element = this.$refs.projects[idx]
      const top = element.offsetTop
      window.scrollTo(0, top)
    },

    hasProject() {
      return this.$config.feature.project
    },

    switchTheme(dark) {
      const mode = dark === true ? 'dark' : 'light'
      if (mode === 'dark') document.body.classList.add('dark')
      else document.body.classList.remove('dark')
      this.$material.theming.theme = `${this.$config.platform.id}-${mode}`
    },
  },
}
</script>
<style lang="scss" scoped>
@import '../../assets/scss/variable.scss';

.page-container {
  max-width: 1100px;
  background: #fff;
  display: table;
  margin: 0 auto;
}
.page-background {
  background: #ddd;
}

.dark {
  .page-container {
    background: $dark-background-color;
  }
  .page-background {
    background: #121212;
  }
}
</style>
